import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, FileText, ChevronRight, MessageCircle, Trash2, RotateCcw } from "lucide-react";
import { getToken, fetchQuotes, dismissQuote, restoreQuote, formatEuro } from "@/lib/mastroAuth";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDismissDialog } from "@/components/ConfirmDismissDialog";
import { QUOTES } from "@/constants/testIds";

export default function Preventivi() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ALL");
  const [confirmQuote, setConfirmQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const load = () => fetchQuotes(getToken(), true).then(setData).catch(() => setData(null));

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const onConfirmDismiss = async () => {
    setBusy(true);
    try {
      await dismissQuote(getToken(), confirmQuote.id);
      await load();
      setConfirmQuote(null);
    } catch {
      // keep dialog open; nothing mutated server-side on failure
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (quoteId) => {
    setRestoringId(quoteId);
    try {
      await restoreQuote(getToken(), quoteId);
      await load();
    } finally {
      setRestoringId(null);
    }
  };

  if (loading) return <div className="flex items-center gap-3 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>;

  const TABS = [
    { key: "ALL", label: "Tutti" },
    { key: "DISMISSED", label: "Eliminati" },
  ];
  const allQuotes = data?.quotes || [];
  const filtered = allQuotes.filter((q) => (tab === "DISMISSED" ? q.dismissed : !q.dismissed));

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <h1 className="text-2xl font-bold tracking-tight">Preventivi</h1>
      <p className="mt-1 text-sm text-neutral-500">Tutti i preventivi generati da Mastro.</p>

      <div data-testid="quotes-tabs" className="mt-5 inline-flex gap-1 rounded-lg border border-neutral-200 bg-neutral-50 p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`quotes-tab-${t.key.toLowerCase()}`}
            onClick={() => setTab(t.key)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === t.key ? "bg-white text-neutral-950 shadow-sm" : "text-neutral-500 hover:text-neutral-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {allQuotes.length === 0 ? (
        <div data-testid={QUOTES.listEmpty} className="mt-6 rounded-2xl border border-neutral-200 bg-white p-8 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
            <MessageCircle className="h-6 w-6" />
          </span>
          <p className="mt-4 text-sm font-semibold text-neutral-950">Nessun preventivo ancora</p>
          <p className="mt-1 text-sm text-neutral-500">Chiedi un preventivo a Mastro su WhatsApp: comparirà qui.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-white px-4 py-10 text-center">
          <p data-testid={`quotes-empty-${tab.toLowerCase()}`} className="text-sm text-neutral-400">
            {tab === "DISMISSED" ? "Nessun preventivo eliminato." : "Nessun preventivo in questa categoria."}
          </p>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-testid={QUOTES.list}>
          <div className="divide-y divide-neutral-100">
            {filtered.map((q) => (
              <div
                key={q.id}
                data-testid={QUOTES.item}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/app/preventivi/${q.id}`)}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/app/preventivi/${q.id}`)}
                className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3.5 text-left transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500 sm:px-5"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium text-neutral-950">
                    <FileText className="h-4 w-4 shrink-0 text-neutral-400" />
                    <span className="truncate">{q.job_title || "Preventivo"}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-neutral-500">
                    v{q.current_version} · Imponibile {formatEuro(q.imponibile)}
                    {q.vat_applied ? ` · IVA ${q.vat_rate}%` : " · senza IVA"}
                  </p>
                </div>
                <div className="ml-1 flex shrink-0 flex-wrap items-center justify-end gap-2 sm:ml-3 sm:gap-3">
                  <span className="hidden text-sm font-semibold text-neutral-950 sm:inline">{formatEuro(q.total)}</span>
                  <StatusBadge status={q.status} />
                  {!q.dismissed && q.status === "CANCELLED" && (
                    <button
                      data-testid={QUOTES.dismiss}
                      onClick={(e) => { e.stopPropagation(); setConfirmQuote(q); }}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Elimina
                    </button>
                  )}
                  {q.dismissed && (
                    <button
                      data-testid={QUOTES.restore}
                      disabled={restoringId === q.id}
                      onClick={(e) => { e.stopPropagation(); onRestore(q.id); }}
                      className="inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-white px-2.5 py-1 text-xs font-medium text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-50"
                    >
                      {restoringId === q.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RotateCcw className="h-3.5 w-3.5" />} Ripristina
                    </button>
                  )}
                  <ChevronRight className="hidden h-4 w-4 text-neutral-300 sm:block" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDismissDialog
        open={confirmQuote != null}
        onOpenChange={(o) => { if (!o && !busy) setConfirmQuote(null); }}
        title={`Eliminare il preventivo di "${confirmQuote?.job_title || "questo lavoro"}"?`}
        description="Il preventivo verrà nascosto dagli elenchi. Nessun dato viene cancellato: versioni e storico restano intatti e potrai ripristinarlo dalla scheda Eliminati."
        busy={busy}
        onConfirm={onConfirmDismiss}
        confirmTestId={QUOTES.dismissConfirm}
        cancelTestId={QUOTES.dismissCancel}
      />
    </motion.div>
  );
}
